import React, { useState, useEffect } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import {
  MapPin, Plus, Edit2, Trash2, Star, Phone, Mail, Building2,
  ArrowRightLeft, Users, Package, RefreshCw, Boxes, AlertTriangle,
  Minus, PlusCircle, Send, Loader2, Search, ArrowRight, X,
} from 'lucide-react';
import axios from '../../axiosConfig';
import './Locations.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_FORM = {
  name: '',
  code: '',
  description: '',
  address: '',
  city: '',
  state: '',
  country: 'Nigeria',
  phone: '',
  email: '',
  timezone: 'Africa/Lagos',
  isActive: true,
  isPrimary: false,
  operatingHours: {
    monday:    { open: '09:00', close: '18:00', closed: false },
    tuesday:   { open: '09:00', close: '18:00', closed: false },
    wednesday: { open: '09:00', close: '18:00', closed: false },
    thursday:  { open: '09:00', close: '18:00', closed: false },
    friday:    { open: '09:00', close: '18:00', closed: false },
    saturday:  { open: '10:00', close: '16:00', closed: false },
    sunday:    { open: '',      close: '',      closed: true  },
  },
};

// Older locations may have an empty or partial `operatingHours` JSON value.
// The edit form always renders all seven days, so normalize each saved value
// before it reaches the inputs instead of assuming every day exists.
const normalizeOperatingHours = (operatingHours) => DAYS.reduce((hours, day) => {
  const savedDay = operatingHours && typeof operatingHours === 'object' && !Array.isArray(operatingHours)
    ? operatingHours[day]
    : null;
  hours[day] = {
    ...DEFAULT_FORM.operatingHours[day],
    ...(savedDay && typeof savedDay === 'object' ? savedDay : {}),
  };
  return hours;
}, {});

const TRANSFER_STATUS_PILL = {
  COMPLETED: 'lc-pill-success',
  PENDING:   'lc-pill-warn',
  CANCELLED: 'lc-pill-danger',
};

export default function Locations() {
  // `useSecureAuth` consumed for future use (gating UI based on user); the
  // FeatureGate already covers tier-level access here.
  useSecureAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('locations');

  // Locations
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierInfo, setTierInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  // Transfers
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [transferForm, setTransferForm] = useState({
    fromLocationId: '', toLocationId: '', productId: '', quantity: 1, notes: '',
  });
  const [productStockInfo, setProductStockInfo] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  // Team
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  // Inventory
  const [inventoryLocation, setInventoryLocation] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventorySearch, setInventorySearch] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('restock');
  const [showQuickTransferModal, setShowQuickTransferModal] = useState(false);
  const [quickTransferItem, setQuickTransferItem] = useState(null);
  const [quickTransferTo, setQuickTransferTo] = useState('');
  const [quickTransferQty, setQuickTransferQty] = useState(1);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProductToAdd, setSelectedProductToAdd] = useState('');
  const [addProductQty, setAddProductQty] = useState(0);

  /* ─── Fetch helpers ─────────────────────────────────────────── */
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const r = await axios.get('/locations');
      if (r.data.success) {
        setLocations(r.data.locations || []);
        setTierInfo(r.data.limits || null);
      }
    } catch (e) {
      console.error('Error fetching locations:', e);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransfers = async () => {
    try {
      setLoadingTransfers(true);
      const r = await axios.get('/locations/transfers/list');
      if (r.data.success) setTransfers(r.data.transfers || []);
    } catch (e) {
      console.error('Error fetching transfers:', e);
    } finally {
      setLoadingTransfers(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const r = await axios.get('/merchants/products');
      if (r.data.success) setProducts(r.data.products || []);
    } catch (e) {
      console.error('Error fetching products:', e);
    }
  };

  const fetchProductStock = async (productId) => {
    if (!productId) { setProductStockInfo(null); return; }
    try {
      setLoadingStock(true);
      const r = await axios.get(`/locations/inventory/${productId}`);
      if (r.data.success) setProductStockInfo(r.data);
    } catch (e) {
      console.error('Error fetching product stock:', e);
      setProductStockInfo(null);
    } finally {
      setLoadingStock(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      setLoadingTeam(true);
      const r = await axios.get('/team/members');
      if (r.data.success) setTeamMembers(r.data.members || []);
    } catch (e) {
      console.error('Error fetching team members:', e);
    } finally {
      setLoadingTeam(false);
    }
  };

  const fetchInventory = async (locationId) => {
    if (!locationId) return;
    try {
      setLoadingInventory(true);
      const params = new URLSearchParams();
      if (inventorySearch) params.append('search', inventorySearch);
      if (showLowStockOnly) params.append('lowStock', 'true');
      params.append('limit', '100');
      const r = await axios.get(`/locations/${locationId}/inventory?${params}`);
      if (r.data.success) setInventory(r.data.inventory || []);
    } catch (e) {
      console.error('Error fetching inventory:', e);
      toast.error('Failed to load inventory');
    } finally {
      setLoadingInventory(false);
    }
  };

  useEffect(() => { fetchLocations(); }, []);

  useEffect(() => {
    if (activeTab === 'transfers') {
      fetchTransfers();
      fetchProducts();
    } else if (activeTab === 'team') {
      fetchTeamMembers();
    } else if (activeTab === 'inventory') {
      if (!inventoryLocation && locations.length > 0) {
        const primary = locations.find((l) => l.isPrimary) || locations[0];
        setInventoryLocation(primary.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'inventory' && inventoryLocation) {
      fetchInventory(inventoryLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryLocation, inventorySearch, showLowStockOnly, activeTab]);

  /* ─── Handlers ──────────────────────────────────────────────── */
  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setEditingLocation(null);
  };

  const handleAddLocation = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setFormData({
      ...DEFAULT_FORM,
      ...location,
      operatingHours: normalizeOperatingHours(location.operatingHours),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formData.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    try {
      setSaving(true);
      const fn = editingLocation
        ? axios.put(`/locations/${editingLocation.id}`, formData)
        : axios.post('/locations', formData);
      const r = await fn;
      if (r.data.success) {
        toast.success(editingLocation ? 'Location updated' : 'Location created');
        fetchLocations();
        setShowModal(false);
        resetForm();
      }
    } catch (e) {
      console.error('Error saving location:', e);
      toast.error(e.response?.data?.error || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Delete this location?')) return;
    try {
      setDeleting(locationId);
      const r = await axios.delete(`/locations/${locationId}`);
      if (r.data.success) {
        toast.success('Location deleted');
        fetchLocations();
      }
    } catch (e) {
      console.error('Error deleting location:', e);
      toast.error(e.response?.data?.error || 'Failed to delete location');
    } finally {
      setDeleting(null);
    }
  };

  const handleSetPrimary = async (locationId) => {
    try {
      const r = await axios.post(`/locations/${locationId}/set-primary`);
      if (r.data.success) {
        toast.success('Primary location updated');
        fetchLocations();
      }
    } catch (e) {
      console.error('Error setting primary:', e);
      toast.error('Failed to set primary location');
    }
  };

  const handleCreateTransfer = async (e) => {
    e?.preventDefault?.();
    if (!transferForm.fromLocationId || !transferForm.toLocationId || !transferForm.productId) {
      toast.error('Fill all required fields');
      return;
    }
    if (transferForm.fromLocationId === transferForm.toLocationId) {
      toast.error('Source and destination must differ');
      return;
    }
    try {
      setSaving(true);
      const r = await axios.post('/locations/transfers', transferForm);
      if (r.data.success) {
        toast.success('Stock transferred');
        closeTransferModal();
        fetchTransfers();
      }
    } catch (e) {
      console.error('Error creating transfer:', e);
      toast.error(e.response?.data?.error || 'Failed to create transfer');
    } finally {
      setSaving(false);
    }
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setTransferForm({ fromLocationId: '', toLocationId: '', productId: '', quantity: 1, notes: '' });
    setProductStockInfo(null);
  };

  const handleAssignTeamMember = async (memberId, locationIds) => {
    try {
      setSaving(true);
      const r = await axios.post('/locations/team/assign', {
        teamMemberId: memberId, locationIds,
      });
      if (r.data.success) {
        toast.success('Team member assigned');
        setShowAssignModal(false);
        setSelectedMember(null);
        fetchTeamMembers();
      }
    } catch (e) {
      console.error('Error assigning team member:', e);
      toast.error(e.response?.data?.error || 'Failed to assign team member');
    } finally {
      setSaving(false);
    }
  };

  const updateOperatingHours = (day, field, value) => {
    setFormData((p) => ({
      ...p,
      operatingHours: {
        ...p.operatingHours,
        [day]: { ...p.operatingHours[day], [field]: value },
      },
    }));
  };

  /* Inventory handlers */
  const openAdjustModal = (item) => {
    setAdjustingItem(item);
    setAdjustmentAmount(0);
    setAdjustmentReason('restock');
    setShowAdjustModal(true);
  };

  const handleStockAdjustment = async () => {
    if (!adjustingItem || adjustmentAmount === 0) return;
    const newQty = Math.max(0, (adjustingItem.quantity || 0) + adjustmentAmount);
    try {
      setSaving(true);
      const r = await axios.put(
        `/locations/${inventoryLocation}/inventory/${adjustingItem.product.id}`,
        { quantity: newQty, reason: adjustmentReason }
      );
      if (r.data.success) {
        toast.success(`Stock ${adjustmentAmount > 0 ? 'increased' : 'decreased'}`);
        setShowAdjustModal(false);
        setAdjustingItem(null);
        fetchInventory(inventoryLocation);
      }
    } catch (e) {
      console.error('Error adjusting stock:', e);
      toast.error(e.response?.data?.error || 'Failed to adjust stock');
    } finally {
      setSaving(false);
    }
  };

  const openQuickTransferModal = (item) => {
    setQuickTransferItem(item);
    setQuickTransferTo('');
    setQuickTransferQty(1);
    setShowQuickTransferModal(true);
  };

  const handleQuickTransfer = async () => {
    if (!quickTransferItem || !quickTransferTo || quickTransferQty < 1) return;
    try {
      setSaving(true);
      const r = await axios.post('/locations/transfers', {
        fromLocationId: inventoryLocation,
        toLocationId: quickTransferTo,
        productId: quickTransferItem.product.id,
        quantity: quickTransferQty,
        notes: 'Quick transfer from inventory management',
      });
      if (r.data.success) {
        toast.success('Transferred');
        setShowQuickTransferModal(false);
        setQuickTransferItem(null);
        fetchInventory(inventoryLocation);
      }
    } catch (e) {
      console.error('Error creating quick transfer:', e);
      toast.error(e.response?.data?.error || 'Failed to create transfer');
    } finally {
      setSaving(false);
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const r = await axios.get('/merchants/products');
      if (r.data.success) {
        const inventoryProductIds = inventory.map((inv) => inv.product?.id);
        setAvailableProducts(
          (r.data.products || []).filter((p) => !inventoryProductIds.includes(p.id) && p.status === 'ACTIVE')
        );
      }
    } catch (e) {
      console.error('Error fetching products:', e);
    }
  };

  const openAddProductModal = () => {
    setSelectedProductToAdd('');
    setAddProductQty(0);
    fetchAvailableProducts();
    setShowAddProductModal(true);
  };

  const handleAddProductToLocation = async () => {
    if (!selectedProductToAdd || addProductQty < 0) return;
    try {
      setSaving(true);
      const r = await axios.put(
        `/locations/${inventoryLocation}/inventory/${selectedProductToAdd}`,
        { quantity: addProductQty, reason: 'initial_stock' }
      );
      if (r.data.success) {
        toast.success('Product added');
        setShowAddProductModal(false);
        fetchInventory(inventoryLocation);
      }
    } catch (e) {
      console.error('Error adding product:', e);
      toast.error(e.response?.data?.error || 'Failed to add product');
    } finally {
      setSaving(false);
    }
  };

  const canAddLocation = tierInfo ? tierInfo.current < tierInfo.max : true;

  if (loading) {
    return (
      <Shell>
        <div className="lc-page">
          <div className="lc-loading"><Loader2 size={20} className="lc-spin" /> Loading locations…</div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <FeatureGate
        featureId="store.locations"
        fallback={
          <div className="lc-page">
            <div className="lc-empty">
              <div className="lc-empty-icon"><MapPin size={22} /></div>
              <h2 className="lc-empty-title">Multi-location management</h2>
              <p className="lc-empty-desc">
                Manage multiple locations, per-location inventory, and team assignments.
                Available on PRO and PREMIUM plans.
              </p>
            </div>
          </div>
        }
      >
        <div className="lc-page">

          {/* ── Header ───────────────────────────────────────────── */}
          <header className="lc-header">
            <div className="lc-header-text">
              <h1 className="lc-title">Locations</h1>
              <p className="lc-subtitle">Branches, stock transfers between them, and which team member works where.</p>
            </div>
          </header>

          {/* ── Tabs ─────────────────────────────────────────────── */}
          <div className="lc-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'locations'}
              className={`lc-tab${activeTab === 'locations' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('locations')}
            >Locations</button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'transfers'}
              className={`lc-tab${activeTab === 'transfers' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('transfers')}
              disabled={locations.length < 2}
              title={locations.length < 2 ? 'Need at least 2 locations' : undefined}
            >Transfers</button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'team'}
              className={`lc-tab${activeTab === 'team' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('team')}
            >Team</button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'inventory'}
              className={`lc-tab${activeTab === 'inventory' ? ' is-active' : ''}`}
              onClick={() => setActiveTab('inventory')}
              disabled={locations.length === 0}
              title={locations.length === 0 ? 'Add a location first' : undefined}
            >Inventory</button>
          </div>

          {/* ── Locations tab ────────────────────────────────────── */}
          {activeTab === 'locations' && (
            <>
              <div className="lc-tab-bar">
                {tierInfo && (
                  <div className="lc-tier">
                    <span className="lc-tier-text">
                      <strong>{tierInfo.current}</strong> of {tierInfo.max} locations used
                    </span>
                    <div className="lc-tier-track">
                      <div className="lc-tier-fill" style={{ width: `${Math.min(100, (tierInfo.current / tierInfo.max) * 100)}%` }} />
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  className="lc-btn lc-btn-primary"
                  onClick={handleAddLocation}
                  disabled={!canAddLocation}
                >
                  <Plus size={14} /> Add location
                </button>
              </div>

              {tierInfo && tierInfo.current >= tierInfo.max && (
                <div className="lc-banner lc-banner-warn">
                  <AlertTriangle size={16} />
                  <span>
                    You've hit the {tierInfo.max}-location limit for your {tierInfo.tier} plan.{' '}
                    <a href="/settings?tab=billing" className="lc-banner-link">Upgrade</a> to add more.
                  </span>
                </div>
              )}

              {locations.length === 0 ? (
                <div className="lc-empty">
                  <div className="lc-empty-icon"><Building2 size={22} /></div>
                  <h2 className="lc-empty-title">No locations yet</h2>
                  <p className="lc-empty-desc">Add your first business location to get started.</p>
                  <button type="button" className="lc-btn lc-btn-primary" onClick={handleAddLocation}>
                    <Plus size={14} /> Add your first location
                  </button>
                </div>
              ) : (
                <div className="lc-grid">
                  {locations.map((location) => (
                    <article key={location.id} className={`lc-loc${location.isPrimary ? ' is-primary' : ''}`}>
                      <div className="lc-loc-head">
                        <div className="lc-loc-head-text">
                          <h3 className="lc-loc-name">
                            {location.name}
                            {location.code && <span className="lc-loc-code">{location.code}</span>}
                          </h3>
                          <div className="lc-loc-pills">
                            {location.isPrimary && (
                              <span className="lc-pill lc-pill-primary"><Star size={10} /> Primary</span>
                            )}
                            <span className={`lc-pill ${location.isActive ? 'lc-pill-success' : 'lc-pill-muted'}`}>
                              {location.isActive ? 'Active' : 'Inactive'}
                            </span>
                            {location.adminStatus === 'SUSPENDED' && (
                              <span className="lc-pill lc-pill-danger">Suspended</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <dl className="lc-loc-details">
                        {location.address && (
                          <div className="lc-loc-detail">
                            <dt><MapPin size={12} /></dt>
                            <dd>{location.address}{location.city ? `, ${location.city}` : ''}</dd>
                          </div>
                        )}
                        {location.phone && (
                          <div className="lc-loc-detail">
                            <dt><Phone size={12} /></dt>
                            <dd>{location.phone}</dd>
                          </div>
                        )}
                        {location.email && (
                          <div className="lc-loc-detail">
                            <dt><Mail size={12} /></dt>
                            <dd>{location.email}</dd>
                          </div>
                        )}
                      </dl>

                      <div className="lc-loc-actions">
                        {!location.isPrimary && (
                          <button type="button" className="lc-btn lc-btn-ghost" onClick={() => handleSetPrimary(location.id)}>
                            <Star size={13} /> Make primary
                          </button>
                        )}
                        <button type="button" className="lc-btn lc-btn-ghost" onClick={() => handleEditLocation(location)}>
                          <Edit2 size={13} /> Edit
                        </button>
                        {!location.isPrimary && (
                          <button
                            type="button"
                            className="lc-btn lc-btn-danger"
                            onClick={() => handleDelete(location.id)}
                            disabled={deleting === location.id}
                          >
                            {deleting === location.id ? <Loader2 size={13} className="lc-spin" /> : <Trash2 size={13} />}
                            Delete
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── Transfers tab ────────────────────────────────────── */}
          {activeTab === 'transfers' && (
            <>
              <div className="lc-tab-bar">
                <p className="lc-tab-bar-text">Move stock between locations.</p>
                <button
                  type="button"
                  className="lc-btn lc-btn-primary"
                  onClick={() => setShowTransferModal(true)}
                  disabled={locations.length < 2}
                >
                  <Plus size={14} /> New transfer
                </button>
              </div>

              {locations.length < 2 ? (
                <div className="lc-empty">
                  <div className="lc-empty-icon"><ArrowRightLeft size={22} /></div>
                  <h2 className="lc-empty-title">Need at least 2 locations</h2>
                  <p className="lc-empty-desc">Add another location before transferring stock.</p>
                </div>
              ) : loadingTransfers ? (
                <div className="lc-loading"><Loader2 size={20} className="lc-spin" /> Loading transfers…</div>
              ) : transfers.length === 0 ? (
                <div className="lc-empty">
                  <div className="lc-empty-icon"><ArrowRightLeft size={22} /></div>
                  <h2 className="lc-empty-title">No transfers yet</h2>
                  <p className="lc-empty-desc">Move stock between locations to keep inventory accurate.</p>
                  <button type="button" className="lc-btn lc-btn-primary" onClick={() => setShowTransferModal(true)}>
                    <Plus size={14} /> Create transfer
                  </button>
                </div>
              ) : (
                <ul className="lc-list">
                  {transfers.map((t) => (
                    <li key={t.id} className="lc-row">
                      <div className="lc-row-main">
                        <div className="lc-tr-head">
                          <span className="lc-tr-product">
                            <Package size={14} aria-hidden="true" />
                            <span>{t.product?.name || 'Unknown product'}</span>
                            <span className="lc-tr-qty">× {t.quantity}</span>
                          </span>
                          <span className={`lc-pill ${TRANSFER_STATUS_PILL[t.status] || 'lc-pill-muted'}`}>
                            {t.status}
                          </span>
                        </div>
                        <div className="lc-tr-route">
                          <span className="lc-tr-loc"><MapPin size={11} /> {t.fromLocation?.name || '—'}</span>
                          <ArrowRight size={12} className="lc-tr-arrow" />
                          <span className="lc-tr-loc"><MapPin size={11} /> {t.toLocation?.name || '—'}</span>
                          <span className="lc-tr-date">{new Date(t.createdAt).toLocaleDateString()}</span>
                        </div>
                        {t.notes && <p className="lc-tr-notes">{t.notes}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* ── Team tab ─────────────────────────────────────────── */}
          {activeTab === 'team' && (
            <>
              <div className="lc-tab-bar">
                <p className="lc-tab-bar-text">Which locations each team member can access.</p>
                <button type="button" className="lc-btn lc-btn-ghost" onClick={fetchTeamMembers} aria-label="Refresh">
                  <RefreshCw size={14} /> <span className="lc-btn-text">Refresh</span>
                </button>
              </div>

              {loadingTeam ? (
                <div className="lc-loading"><Loader2 size={20} className="lc-spin" /> Loading team…</div>
              ) : teamMembers.length === 0 ? (
                <div className="lc-empty">
                  <div className="lc-empty-icon"><Users size={22} /></div>
                  <h2 className="lc-empty-title">No team members yet</h2>
                  <p className="lc-empty-desc">Add team members from the Team page first, then assign them to locations.</p>
                  <a href="/team" className="lc-btn lc-btn-primary">Go to Team</a>
                </div>
              ) : (
                <ul className="lc-list">
                  {teamMembers.map((member) => (
                    <li key={member.id} className="lc-row">
                      <div className="lc-row-main lc-member">
                        <div className="lc-member-avatar">
                          {(member.user?.username || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="lc-member-info">
                          <div className="lc-member-name">
                            {member.user?.username || 'Unknown'}
                            <span className="lc-member-role">{member.role?.name || 'No role'}</span>
                          </div>
                          <div className="lc-member-email">{member.user?.email || '—'}</div>
                          <div className="lc-member-locs">
                            {member.locationAssignments?.length > 0 ? (
                              member.locationAssignments.map((a) => (
                                <span key={a.id} className="lc-pill lc-pill-muted">
                                  <MapPin size={10} /> {a.location?.name}
                                </span>
                              ))
                            ) : (
                              <span className="lc-pill lc-pill-info">All locations</span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="lc-btn lc-btn-secondary"
                          onClick={() => { setSelectedMember(member); setShowAssignModal(true); }}
                        >
                          <Edit2 size={13} /> Manage
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {/* ── Inventory tab ────────────────────────────────────── */}
          {activeTab === 'inventory' && (
            <>
              <div className="lc-tab-bar">
                <p className="lc-tab-bar-text">Stock levels per location.</p>
                <div className="lc-tab-bar-actions">
                  <button type="button" className="lc-btn lc-btn-ghost" onClick={() => fetchInventory(inventoryLocation)} aria-label="Refresh">
                    <RefreshCw size={14} /> <span className="lc-btn-text">Refresh</span>
                  </button>
                  <button
                    type="button"
                    className="lc-btn lc-btn-primary"
                    onClick={openAddProductModal}
                    disabled={!inventoryLocation}
                  >
                    <PlusCircle size={14} /> Add product
                  </button>
                </div>
              </div>

              {locations.length === 0 ? (
                <div className="lc-empty">
                  <div className="lc-empty-icon"><Boxes size={22} /></div>
                  <h2 className="lc-empty-title">No locations</h2>
                  <p className="lc-empty-desc">Add locations first, then come back here to manage inventory.</p>
                </div>
              ) : (
                <>
                  <section className="lc-filters">
                    <select
                      className="lc-input lc-select"
                      value={inventoryLocation || ''}
                      onChange={(e) => setInventoryLocation(e.target.value)}
                    >
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}{loc.isPrimary ? ' (Primary)' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="lc-search">
                      <input
                        type="text"
                        className="lc-input lc-search-input"
                        placeholder="Search products…"
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                      />
                      {inventorySearch && (
                        <button
                          type="button"
                          className="lc-search-clear"
                          onClick={() => setInventorySearch('')}
                          aria-label="Clear search"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <label className="lc-check">
                      <input
                        type="checkbox"
                        checked={showLowStockOnly}
                        onChange={(e) => setShowLowStockOnly(e.target.checked)}
                      />
                      <span>Low stock only</span>
                    </label>
                  </section>

                  {loadingInventory ? (
                    <div className="lc-loading"><Loader2 size={20} className="lc-spin" /> Loading inventory…</div>
                  ) : inventory.length === 0 ? (
                    <div className="lc-empty">
                      <div className="lc-empty-icon"><Package size={22} /></div>
                      <h2 className="lc-empty-title">
                        {inventorySearch || showLowStockOnly ? 'No matches' : 'No inventory yet'}
                      </h2>
                      <p className="lc-empty-desc">
                        {inventorySearch || showLowStockOnly
                          ? 'No products match these filters.'
                          : 'Add products to this location to start tracking stock.'}
                      </p>
                    </div>
                  ) : (
                    <ul className="lc-list">
                      {inventory.map((item) => {
                        const locQty = item.quantity || 0;
                        const globalQty = item.product?.quantity || 0;
                        const isLow = locQty > 0 && locQty <= 5;
                        const isOut = locQty === 0;
                        return (
                          <li key={item.id} className="lc-row">
                            <div className="lc-row-main lc-inv-row">
                              <span className="lc-inv-name">{item.product?.name || 'Unknown'}</span>
                              <span className="lc-inv-qty-cell">
                                <span className={`lc-inv-qty${isOut ? ' is-zero' : isLow ? ' is-low' : ''}`}>
                                  {locQty}
                                </span>
                                <span className="lc-inv-of">/ {globalQty}</span>
                              </span>
                              {/* `lc-inv-meta` groups sku + status on mobile (one line
                                  under the name). On ≥720px display:contents flattens
                                  it so each child becomes a direct grid item. */}
                              <span className="lc-inv-meta">
                                <span className="lc-inv-sku">{item.product?.sku || '—'}</span>
                                <span className={`lc-pill ${isOut ? 'lc-pill-danger' : isLow ? 'lc-pill-warn' : 'lc-pill-success'}`}>
                                  {isOut ? 'Out' : isLow ? 'Low' : 'In stock'}
                                </span>
                              </span>
                              <div className="lc-inv-actions">
                                <button
                                  type="button"
                                  className="lc-icon-btn"
                                  onClick={() => openAdjustModal(item)}
                                  aria-label="Adjust stock"
                                  title="Adjust"
                                >
                                  <Edit2 size={13} />
                                </button>
                                {locations.length > 1 && locQty > 0 && (
                                  <button
                                    type="button"
                                    className="lc-icon-btn"
                                    onClick={() => openQuickTransferModal(item)}
                                    aria-label="Quick transfer"
                                    title="Transfer"
                                  >
                                    <Send size={13} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Add / Edit Location Modal ─────────────────────────── */}
          <Modal
            isOpen={showModal}
            onClose={() => { setShowModal(false); resetForm(); }}
            title={editingLocation ? 'Edit location' : 'Add location'}
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="lc-btn lc-btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="button" className="lc-btn lc-btn-primary" onClick={handleSubmit} disabled={saving}>
                  {saving ? <Loader2 size={14} className="lc-spin" /> : null}
                  {editingLocation ? 'Update location' : 'Create location'}
                </button>
              </>
            }
          >
            <form onSubmit={handleSubmit} className="lc-form">
              <section className="lc-form-section">
                <h3 className="lc-form-section-title">Basics</h3>
                <div className="lc-form-row">
                  <div className="lc-field">
                    <label className="lc-field-label">Name *</label>
                    <input
                      type="text"
                      className="lc-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Victoria Island Store"
                      required
                    />
                  </div>
                  <div className="lc-field">
                    <label className="lc-field-label">Code</label>
                    <input
                      type="text"
                      className="lc-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="VI"
                      maxLength={10}
                    />
                  </div>
                </div>
                <div className="lc-field">
                  <label className="lc-field-label">Description</label>
                  <textarea
                    className="lc-input lc-textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description"
                    rows={2}
                  />
                </div>
              </section>

              <section className="lc-form-section">
                <h3 className="lc-form-section-title">Contact</h3>
                <div className="lc-field">
                  <label className="lc-field-label">Address</label>
                  <input
                    type="text"
                    className="lc-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street address"
                  />
                </div>
                <div className="lc-form-row">
                  <div className="lc-field">
                    <label className="lc-field-label">City</label>
                    <input
                      type="text"
                      className="lc-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="lc-field">
                    <label className="lc-field-label">State</label>
                    <input
                      type="text"
                      className="lc-input"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                </div>
                <div className="lc-form-row">
                  <div className="lc-field">
                    <label className="lc-field-label">Phone</label>
                    <input
                      type="tel"
                      className="lc-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+234…"
                    />
                  </div>
                  <div className="lc-field">
                    <label className="lc-field-label">Email</label>
                    <input
                      type="email"
                      className="lc-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="location@example.com"
                    />
                  </div>
                </div>
              </section>

              <section className="lc-form-section">
                <h3 className="lc-form-section-title">Operating hours</h3>
                <div className="lc-hours">
                  {DAYS.map((day) => {
                    const h = formData.operatingHours[day];
                    return (
                      <div key={day} className="lc-hours-row">
                        <span className="lc-hours-day">{day.slice(0, 3).toUpperCase()}</span>
                        <label className="lc-hours-toggle">
                          <input
                            type="checkbox"
                            checked={!h.closed}
                            onChange={(e) => updateOperatingHours(day, 'closed', !e.target.checked)}
                          />
                          <span>{h.closed ? 'Closed' : 'Open'}</span>
                        </label>
                        {!h.closed && (
                          <span className="lc-hours-times">
                            <input
                              type="time"
                              className="lc-input lc-hours-time"
                              value={h.open}
                              onChange={(e) => updateOperatingHours(day, 'open', e.target.value)}
                            />
                            <span className="lc-hours-sep">–</span>
                            <input
                              type="time"
                              className="lc-input lc-hours-time"
                              value={h.close}
                              onChange={(e) => updateOperatingHours(day, 'close', e.target.value)}
                            />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="lc-form-section">
                <h3 className="lc-form-section-title">Settings</h3>
                <div className="lc-toggle-row">
                  <div className="lc-toggle-text">
                    <span className="lc-toggle-label">Active</span>
                    <span className="lc-toggle-sub">Inactive locations are hidden from POS and team assignments.</span>
                  </div>
                  <label className="lc-toggle">
                    <input
                      type="checkbox"
                      checked={!!formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span className="lc-toggle-track" />
                  </label>
                </div>
                <div className="lc-toggle-row">
                  <div className="lc-toggle-text">
                    <span className="lc-toggle-label">Primary location</span>
                    <span className="lc-toggle-sub">Default location for new orders and team assignments.</span>
                  </div>
                  <label className="lc-toggle">
                    <input
                      type="checkbox"
                      checked={!!formData.isPrimary}
                      onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                    />
                    <span className="lc-toggle-track" />
                  </label>
                </div>
              </section>
            </form>
          </Modal>

          {/* ── Stock Transfer Modal ──────────────────────────────── */}
          <Modal
            isOpen={showTransferModal}
            onClose={closeTransferModal}
            title="Create stock transfer"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="lc-btn lc-btn-secondary" onClick={closeTransferModal}>Cancel</button>
                <button type="button" className="lc-btn lc-btn-primary" onClick={handleCreateTransfer} disabled={saving}>
                  {saving ? <Loader2 size={14} className="lc-spin" /> : <ArrowRightLeft size={14} />}
                  Transfer
                </button>
              </>
            }
          >
            <form onSubmit={handleCreateTransfer} className="lc-form">
              <div className="lc-form-row">
                <div className="lc-field">
                  <label className="lc-field-label">From</label>
                  <select
                    className="lc-input lc-select"
                    value={transferForm.fromLocationId}
                    onChange={(e) => setTransferForm({ ...transferForm, fromLocationId: e.target.value })}
                    required
                  >
                    <option value="">Select source</option>
                    {locations.map((loc) => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                  </select>
                </div>
                <div className="lc-field">
                  <label className="lc-field-label">To</label>
                  <select
                    className="lc-input lc-select"
                    value={transferForm.toLocationId}
                    onChange={(e) => setTransferForm({ ...transferForm, toLocationId: e.target.value })}
                    required
                  >
                    <option value="">Select destination</option>
                    {locations.filter((l) => l.id !== transferForm.fromLocationId).map((loc) =>
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="lc-field">
                <label className="lc-field-label">Product</label>
                <select
                  className="lc-input lc-select"
                  value={transferForm.productId}
                  onChange={(e) => {
                    setTransferForm({ ...transferForm, productId: e.target.value });
                    fetchProductStock(e.target.value);
                  }}
                  required
                >
                  <option value="">Select product</option>
                  {products.map((p) =>
                    <option key={p.id} value={p.id}>{p.name} (stock: {p.stock || 0})</option>
                  )}
                </select>
              </div>

              {loadingStock && <div className="lc-form-hint">Loading stock info…</div>}
              {productStockInfo && transferForm.fromLocationId && (() => {
                const src = productStockInfo.stockByLocation.find((s) => s.locationId === transferForm.fromLocationId);
                const dst = productStockInfo.stockByLocation.find((s) => s.locationId === transferForm.toLocationId);
                const available = src?.available || 0;
                const remaining = available - (transferForm.quantity || 0);
                return (
                  <div className={`lc-banner ${remaining < 0 ? 'lc-banner-error' : 'lc-banner-info'}`}>
                    <div>
                      <strong>Source:</strong> {available} available
                      {transferForm.quantity > 0 && <span>{' → '}{remaining} after transfer</span>}
                      {dst && (
                        <div className="lc-banner-sub">
                          <strong>Dest:</strong> {dst.available} → {dst.available + (transferForm.quantity || 0)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="lc-form-row">
                <div className="lc-field">
                  <label className="lc-field-label">Quantity</label>
                  <input
                    type="number"
                    className="lc-input"
                    min="1"
                    max={productStockInfo?.stockByLocation.find((s) => s.locationId === transferForm.fromLocationId)?.available || 999}
                    value={transferForm.quantity}
                    onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value, 10) || 1 })}
                    required
                  />
                </div>
              </div>

              <div className="lc-field">
                <label className="lc-field-label">Notes (optional)</label>
                <textarea
                  className="lc-input lc-textarea"
                  value={transferForm.notes}
                  onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Anything else worth recording…"
                />
              </div>
            </form>
          </Modal>

          {/* ── Team Assignment Modal ─────────────────────────────── */}
          <Modal
            isOpen={showAssignModal}
            onClose={() => { setShowAssignModal(false); setSelectedMember(null); }}
            title={selectedMember ? `Assign — ${selectedMember.user?.username || ''}` : 'Assign locations'}
            size="md"
            padded={false}
          >
            {selectedMember && (
              <TeamAssignmentForm
                member={selectedMember}
                locations={locations}
                onSave={handleAssignTeamMember}
                onCancel={() => { setShowAssignModal(false); setSelectedMember(null); }}
                saving={saving}
              />
            )}
          </Modal>

          {/* ── Stock Adjust Modal ───────────────────────────────── */}
          <Modal
            isOpen={showAdjustModal}
            onClose={() => { setShowAdjustModal(false); setAdjustingItem(null); }}
            title="Adjust stock"
            size="sm"
            padded={false}
            footer={
              <>
                <button type="button" className="lc-btn lc-btn-secondary" onClick={() => setShowAdjustModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="lc-btn lc-btn-primary"
                  onClick={handleStockAdjustment}
                  disabled={saving || adjustmentAmount === 0}
                >
                  {saving ? <Loader2 size={14} className="lc-spin" /> : null}
                  Apply
                </button>
              </>
            }
          >
            {adjustingItem && (
              <div className="lc-form">
                <div className="lc-adjust-product">
                  <strong>{adjustingItem.product?.name}</strong>
                  <span>Current: {adjustingItem.quantity || 0} units</span>
                </div>

                <div className="lc-field">
                  <label className="lc-field-label">Adjustment</label>
                  <div className="lc-stepper">
                    <button
                      type="button"
                      className="lc-stepper-btn"
                      onClick={() => setAdjustmentAmount((p) => p - 1)}
                      aria-label="Decrease"
                    >
                      <Minus size={14} />
                    </button>
                    <input
                      type="number"
                      className="lc-input lc-stepper-input"
                      value={adjustmentAmount}
                      onChange={(e) => setAdjustmentAmount(parseInt(e.target.value, 10) || 0)}
                    />
                    <button
                      type="button"
                      className="lc-stepper-btn"
                      onClick={() => setAdjustmentAmount((p) => p + 1)}
                      aria-label="Increase"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <span className="lc-form-hint">
                    New quantity: <strong>{Math.max(0, (adjustingItem.quantity || 0) + adjustmentAmount)}</strong>
                    {adjustmentAmount !== 0 && (
                      <span className={adjustmentAmount > 0 ? 'lc-delta-up' : 'lc-delta-down'}>
                        {' '}({adjustmentAmount > 0 ? '+' : ''}{adjustmentAmount})
                      </span>
                    )}
                  </span>
                </div>

                <div className="lc-field">
                  <label className="lc-field-label">Reason</label>
                  <select
                    className="lc-input lc-select"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                  >
                    <option value="restock">Restock / received</option>
                    <option value="damage">Damage / loss</option>
                    <option value="correction">Inventory correction</option>
                    <option value="return">Customer return</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            )}
          </Modal>

          {/* ── Quick Transfer Modal ──────────────────────────────── */}
          <Modal
            isOpen={showQuickTransferModal}
            onClose={() => { setShowQuickTransferModal(false); setQuickTransferItem(null); }}
            title="Quick transfer"
            size="sm"
            padded={false}
            footer={
              <>
                <button type="button" className="lc-btn lc-btn-secondary" onClick={() => setShowQuickTransferModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="lc-btn lc-btn-primary"
                  onClick={handleQuickTransfer}
                  disabled={
                    saving ||
                    !quickTransferTo ||
                    quickTransferQty < 1 ||
                    quickTransferQty > (quickTransferItem?.quantity || 0)
                  }
                >
                  {saving ? <Loader2 size={14} className="lc-spin" /> : <Send size={14} />}
                  Transfer
                </button>
              </>
            }
          >
            {quickTransferItem && (
              <div className="lc-form">
                <div className="lc-adjust-product">
                  <strong>{quickTransferItem.product?.name}</strong>
                  <span>Available: {quickTransferItem.quantity || 0} units</span>
                </div>
                <div className="lc-field">
                  <label className="lc-field-label">Transfer to</label>
                  <select
                    className="lc-input lc-select"
                    value={quickTransferTo}
                    onChange={(e) => setQuickTransferTo(e.target.value)}
                  >
                    <option value="">Select destination</option>
                    {locations.filter((l) => l.id !== inventoryLocation).map((loc) =>
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    )}
                  </select>
                </div>
                <div className="lc-field">
                  <label className="lc-field-label">Quantity</label>
                  <input
                    type="number"
                    className="lc-input"
                    min="1"
                    max={quickTransferItem.quantity || 0}
                    value={quickTransferQty}
                    onChange={(e) => setQuickTransferQty(parseInt(e.target.value, 10) || 1)}
                  />
                </div>
              </div>
            )}
          </Modal>

          {/* ── Add Product to Location Modal ─────────────────────── */}
          <Modal
            isOpen={showAddProductModal}
            onClose={() => setShowAddProductModal(false)}
            title="Add product to location"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="lc-btn lc-btn-secondary" onClick={() => setShowAddProductModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="lc-btn lc-btn-primary"
                  onClick={handleAddProductToLocation}
                  disabled={saving || !selectedProductToAdd}
                >
                  {saving ? <Loader2 size={14} className="lc-spin" /> : <Plus size={14} />}
                  Add
                </button>
              </>
            }
          >
            <div className="lc-form">
              <div className="lc-field">
                <label className="lc-field-label">Product</label>
                <select
                  className="lc-input lc-select"
                  value={selectedProductToAdd}
                  onChange={(e) => setSelectedProductToAdd(e.target.value)}
                >
                  <option value="">Choose a product…</option>
                  {availableProducts.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} (global: {p.quantity || 0})</option>
                  ))}
                </select>
                {availableProducts.length === 0 && (
                  <span className="lc-form-hint">All active products are already at this location.</span>
                )}
              </div>
              <div className="lc-field">
                <label className="lc-field-label">Initial quantity</label>
                <input
                  type="number"
                  className="lc-input"
                  min="0"
                  value={addProductQty}
                  onChange={(e) => setAddProductQty(parseInt(e.target.value, 10) || 0)}
                />
                <span className="lc-form-hint">Added to both location and global stock.</span>
              </div>
            </div>
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}

/* ─── Team Assignment Form (used inside the Assign Modal) ──── */
function TeamAssignmentForm({ member, locations, onSave, onCancel, saving }) {
  const [selectedLocations, setSelectedLocations] = useState(
    member.locationAssignments?.map((a) => a.locationId) || []
  );
  const [assignAll, setAssignAll] = useState(
    !member.locationAssignments || member.locationAssignments.length === 0
  );

  const toggle = (id) =>
    setSelectedLocations((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  return (
    <div className="lc-form">
      <div className="lc-toggle-row">
        <div className="lc-toggle-text">
          <span className="lc-toggle-label">Access all locations</span>
          <span className="lc-toggle-sub">Includes current and any future locations.</span>
        </div>
        <label className="lc-toggle">
          <input
            type="checkbox"
            checked={assignAll}
            onChange={(e) => setAssignAll(e.target.checked)}
          />
          <span className="lc-toggle-track" />
        </label>
      </div>

      {!assignAll && (
        <div className="lc-field">
          <label className="lc-field-label">Specific locations</label>
          <ul className="lc-loc-checks">
            {locations.map((location) => (
              <li key={location.id}>
                <label className="lc-loc-check">
                  <input
                    type="checkbox"
                    checked={selectedLocations.includes(location.id)}
                    onChange={() => toggle(location.id)}
                  />
                  <MapPin size={13} />
                  <span>{location.name}</span>
                  {location.isPrimary && <span className="lc-pill lc-pill-primary">Primary</span>}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="lc-form-foot">
        <button type="button" className="lc-btn lc-btn-secondary" onClick={onCancel}>Cancel</button>
        <button
          type="button"
          className="lc-btn lc-btn-primary"
          onClick={() => onSave(member.id, assignAll ? [] : selectedLocations)}
          disabled={saving}
        >
          {saving ? <Loader2 size={14} className="lc-spin" /> : null}
          Save assignments
        </button>
      </div>
    </div>
  );
}
